import { Grid } from '@material-ui/core';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  fetchDeliveryRestrictions,
  initLenderRequest,
  resetLenderRequest,
  resetRemoveDRWizard
} from '../../../../redux/actions/lenderRequestActions';
import VerticalLinearStepper from '../../../VerticalLinearStepper/VerticalLinearStepper';
import { CPM_REMOVE_DELIVERY_RESTRICTION_REQUEST } from '../../../shared/constants/FeatureToggleKeys';
import { REQUEST_TYPES } from '../../../shared/constants/RequestTypes';
import useFeatureFlags from '../../../shared/services/featureToggle/useFeatureFlags';
import { isRequestingRole } from '../../../../redux/actions/userAction';

// Import page components
import AttachmentsPage from './Pages/AttachmentsPage';
import ReviewAndSubmitPage from './Pages/ReviewAndSubmitPage';
import SelectRestrictionsPage from './Pages/SelectRestrictionsPage';
import SuccessPage from './Pages/SuccessPage';

// Import shared components
import ProjectInfoBanner from '../../../SharedComponents/ProjectInfoBanner/ProjectInfoBanner';

// Import CSS
import '../../../AddNewProject/AddNewProject.css';
import './CreateRemoveDeliveryRestrictionRequest.css';

const CreateRemoveDeliveryRestrictionRequest = () => {
  const history = useHistory();
  const { project_id: projectId } = useParams();
  const dispatch = useDispatch();
  const { isFeatureEnabled } = useFeatureFlags([CPM_REMOVE_DELIVERY_RESTRICTION_REQUEST]);
  const [projectInfo, setProjectInfo] = useState(null);

  const [stepCount, setStepCount] = useState(0);
  const [formState, setFormState] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize the lender request (fetches request subtypes/doc types) once on mount
  useEffect(() => {
    dispatch(resetLenderRequest());
    dispatch(initLenderRequest(REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION));
  }, [dispatch]);

  // Fetch project information on component mount
  useEffect(() => {    
    if (projectId) {
      fetchProjectInfo();
    }
  }, [projectId]);
  
  async function fetchProjectInfo() {
    try {
      setLoading(true);

      const { data: project } = await axios.get(
          global.REACT_APP_API_ENDPOINT +
          `/condoMgr/getProjectDetailsByProjectId/` +
          projectId
      );

      if (!project || project === `Cannot find project:${projectId}`) {
          console.warn(project);
          return;
      }

      setProjectInfo(project.projectProfile);
      setError(null);
    } catch (err) {
      console.error("Error fetching project info:", err);
      setError("Failed to load project information");
    } finally {
      setLoading(false);
    }
  };

  // Fetch delivery restrictions on mount
  useEffect(() => {
    if (projectId) {
      dispatch(fetchDeliveryRestrictions(projectId));
      setLoading(false);
    }

    // Cleanup on unmount
    return () => {
      dispatch(resetRemoveDRWizard());
    };
  }, [projectId, dispatch]);

  // Redirect unauthorized users. Runs in an effect (not during render) so that
  // history.replace() doesn't trigger a synchronous re-render loop.
  useEffect(() => {
    if (!isRequestingRole() 
    //  || !isFeatureEnabled(CPM_REMOVE_DELIVERY_RESTRICTION_REQUEST)
    ) {
      history.replace('/unauthorized');
    }
  }, [history]);

  const steps = [
    'Select Restrictions',
    'Attach Documents',
    'Review & Submit',
    'Confirmation'
  ];

  // Check permissions
  if (!isRequestingRole()) {
    return null;
  }

  const incrementCounter = (formStateData) => {
    if (formStateData) {
      setFormState(formStateData);
    }
    setStepCount(stepCount + 1);
  };

  const decrementCounter = (formStateData) => {
    if (formStateData) {
      setFormState(formStateData);
    }
    setStepCount(stepCount - 1);
  };

  const renderStepContent = () => {
    switch (stepCount) {
      case 0:
        return (
          <SelectRestrictionsPage
            projectId={projectId}
            formState={formState}
            incrementCounter={incrementCounter}
          />
        );
      case 1:
        return (
          <AttachmentsPage
            formState={formState}
            incrementCounter={incrementCounter}
            decrementCounter={decrementCounter}
          />
        );
      case 2:
        return (
          <ReviewAndSubmitPage
            projectId={projectId}
            projectInfo={projectInfo}
            formState={formState}
            incrementCounter={incrementCounter}
            decrementCounter={decrementCounter}
          />
        );
      case 3:
        return <SuccessPage projectId={projectId} />;
      default:
        return null;
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (error) {
    return (
      <Grid container>
        <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
          <VerticalLinearStepper
            steps={steps}
            activeStep={stepCount}
          />
        </Grid>
        <Grid
          container
          item
          xs={9}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          style={{ padding: "20px", border: "solid 1px black" }}
        >
          <div>
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => history.goBack()}>Go Back</button>
          </div>
        </Grid>
      </Grid>
    );
  }

  return (    
    <Grid container className="create-request-container remove-delivery-restriction-request">
      <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
        <VerticalLinearStepper
          steps={steps}
          activeStep={stepCount}
        />
      </Grid>
      <Grid
        container
        item
        xs={9}
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ padding: "20px", border: "solid 1px black" }}
      >
        <ProjectInfoBanner projectId={projectId} projectName={projectInfo?.projectName} />
        {renderStepContent()}
      </Grid>
    </Grid>
  );
};

export default CreateRemoveDeliveryRestrictionRequest;
